import type { RetrieveThreadEntityLinksResponse, SiteData } from '@frontend/api-types';
import type { BackoffConfig } from '@frontend/duck-tape';
import { calculateBackoffInterval } from '@frontend/duck-tape';
import { useCallback, useEffect, useRef, useState } from 'react';

type UseThreadLinksProps = {
  retrieveThreadEntityLinks: (
    uuid: string,
    useCache: boolean,
  ) => Promise<MaybeUndefined<RetrieveThreadEntityLinksResponse>>;
};

const backoffConfig: BackoffConfig = {
  baseExponentialInterval: 5000,
  extendedAttempts: 5,
  extendedInterval: 3000,
  initialAttempts: 5,
  initialInterval: 1000,
};

const maxAttempts = 10;

export const useThreadLinks = ({ retrieveThreadEntityLinks }: UseThreadLinksProps) => {
  const [linksData, setLinksData] = useState<{
    selectedText: MaybeUndefined<string>;
    siteData: SiteData[];
    status: MaybeUndefined<RetrieveThreadEntityLinksResponse['status']>;
  }>({
    selectedText: undefined,
    siteData: [],
    status: undefined,
  });
  const [isThreadEntityLinksPollingLoading, setIsThreadEntityLinksPollingLoading] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const clearCurrentTimeout = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      // eslint-disable-next-line fp/no-mutation
      timeoutRef.current = null;
    }
  }, []);

  useEffect(() => {
    clearCurrentTimeout();
    setIsThreadEntityLinksPollingLoading(false);
    return () => {
      clearCurrentTimeout();
    };
  }, [clearCurrentTimeout]);

  const pollThreadEntityLinks = async (uuid: string, currentAttempt: number): Promise<void> => {
    const threadEntityLinks = await retrieveThreadEntityLinks(uuid, currentAttempt === 1);

    if (threadEntityLinks?.status !== 'in_progress') {
      setIsThreadEntityLinksPollingLoading(false);
      setLinksData((prev) => ({ ...prev, siteData: threadEntityLinks?.results ?? prev.siteData }));
    } else if (currentAttempt >= maxAttempts) {
      setIsThreadEntityLinksPollingLoading(false);
      setLinksData((prev) => ({
        ...prev,
        siteData: threadEntityLinks.results ?? prev.siteData,
        status: threadEntityLinks.status,
      }));
    } else {
      const interval = calculateBackoffInterval(currentAttempt, backoffConfig);
      clearCurrentTimeout();
      // eslint-disable-next-line fp/no-mutation
      timeoutRef.current = setTimeout(() => pollThreadEntityLinks(uuid, currentAttempt + 1), interval);
    }
  };

  const queryThreadEntityLinks = async ({ text, uuid }: { text: string; uuid: string }) => {
    setLinksData((prev) => ({ ...prev, selectedText: text, siteData: prev.siteData }));
    setIsThreadEntityLinksPollingLoading(true);
    pollThreadEntityLinks(uuid, 1);
  };

  return {
    isThreadEntityLinksPollingLoading,
    linksData,
    queryThreadEntityLinks,
    setLinksData,
  };
};
